import React from "react";
import { useLocation, useHistory } from "react-router-dom";
import classnames from "classnames";
import qs, { ParsedQs } from "qs";
import LazyLoad from "react-lazy-load";

import "./RenditionViewer.css";

import { Button } from "../../../lib/Button";
import { Icon, IconGlyph } from "../../../lib/Icon";

import { RenditionCodes } from "../../../types";

import { useToggle } from "../../../hooks";

interface IQuery extends ParsedQs {
  pageIndex: string;
}

interface IRenditionProps {
  asset: IRenditionAsset;
}

interface IThumbnailImageProps {
  src: string;
  className: string;
  onClick: () => void;
  label: number;
  height: number;
}

interface IRenditionImageProps {
  src: string;
  label: number;
  height: number;
  style: {
    height: number | string;
    width: number | string;
  };
}

const ThumbnailImage = React.forwardRef<HTMLLIElement, IThumbnailImageProps>(
  ({ src, className, onClick, label, height }, ref) => (
    <li onClick={onClick} className={className} ref={ref}>
      <LazyLoad height={height} offsetVertical={height}>
        <img
          className="mv-asset-viewer__small-renditions-thumbnail checkerboard-background"
          src={src}
          alt={`Thumbnail preview of page ${label} of file`}
        />
      </LazyLoad>
      <span>{label}</span>
    </li>
  )
);

const RenditionImage = React.forwardRef<HTMLLIElement, IRenditionImageProps>(
  ({ src, label, height, style }, ref) => (
    <li ref={ref}>
      <LazyLoad height={height}>
        <img
          className="mv-asset-viewer__rendition checkerboard-background"
          src={src}
          alt={`Active view of page ${label} of file`}
          style={style}
        />
      </LazyLoad>
    </li>
  )
);

const RenditionViewer = ({ asset }: IRenditionProps) => {
  const location = useLocation();
  const history = useHistory();
  const { isOpen: isDrawerOpen, onToggle: onToggleDrawer } = useToggle(true);
  const {
    isOpen: isFullWidth,
    onToggle: onToggleWidth,
    onOpen: setFullWidth,
  } = useToggle(true);
  const [scrolling, setIsScrolling] = React.useState(false);

  const renditionMap = React.useMemo(() => {
    return {
      [RenditionCodes.SMALL]: asset?.renditions?.find(
        (rendition) => rendition.code === RenditionCodes.SMALL
      ),
      [RenditionCodes.MEDIUM]: asset?.renditions?.find(
        (rendition) => rendition.code === RenditionCodes.MEDIUM
      ),
      [RenditionCodes.LARGE]: asset?.renditions?.find(
        (rendition) => rendition.code === RenditionCodes.LARGE
      ),
    };
  }, [asset]);

  const renditionCode = isFullWidth
    ? RenditionCodes.LARGE
    : RenditionCodes.MEDIUM;

  const smallRendition = renditionMap[RenditionCodes.SMALL];

  const selectedRendition = renditionMap[renditionCode];

  const hasPage = (pageValue: number) =>
    pageValue > 0 && pageValue <= selectedRendition?.pages.count!;

  const { pageIndex } = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  }) as IQuery;
  const pageIndexValue = parseInt(pageIndex);

  const initialPageValue: number =
    !!pageIndexValue && hasPage(pageIndexValue) ? pageIndexValue : 1;

  const [currentPageValue, setCurrentPageValue] = React.useState(
    initialPageValue
  );
  const [sequence, setSequence] = React.useState(initialPageValue - 1);

  const thumbnailStyles = () =>
    classnames(
      "mv-asset-viewer__small-renditions",
      !isDrawerOpen ? "mv-asset-viewer__small-renditions--closed" : ""
    );

  const renditionRefs = React.useRef<React.RefObject<HTMLLIElement>[]>([]);
  renditionRefs.current = [];
  selectedRendition?.pages.renditions.forEach((_rendition, index) => {
    renditionRefs.current[index] = React.createRef<HTMLLIElement>();
  });
  const renditionParentRef = React.useRef<HTMLUListElement>(null);

  const thumbnailRefs = React.useRef<React.RefObject<HTMLLIElement>[]>([]);
  thumbnailRefs.current = [];
  selectedRendition?.pages.renditions.forEach((_rendition, index) => {
    thumbnailRefs.current[index] = React.createRef<HTMLLIElement>();
  });

  const scrollToRendition = (pageValue: number) => {
    const index = pageValue - 1;
    const parentRef = renditionParentRef.current;
    const selectedRef = renditionRefs.current[index]?.current;
    if (parentRef && selectedRef) {
      parentRef.scrollTop = selectedRef.offsetTop;
    }
  };

  const scrollToThumbnail = (index: number) => {
    const selectedThumbnailRef = thumbnailRefs.current[index]?.current;
    if (selectedThumbnailRef) {
      selectedThumbnailRef.scrollIntoView({
        block: "nearest",
        inline: "center",
      });
    }
  };

  const setPage = (value: number) => {
    let selectedSequence = value - 1;
    if (hasPage(value)) {
      history.push(location.pathname + "?pageIndex=" + value);
      setSequence(selectedSequence);
      setCurrentPageValue(value);
    } else {
      setCurrentPageValue(sequence + 1);
    }
  };

  const handleThumbnailClick = (index: number) => {
    const page = index + 1;
    if (renditionRefs.current[index]?.current) {
      setPage(page);
    }
    scrollToRendition(page);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setCurrentPageValue(parseInt(e.currentTarget.value));

  const handleInputBlur = () => {
    setPage(currentPageValue);
    scrollToRendition(currentPageValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      setPage(currentPageValue);
      scrollToRendition(currentPageValue);
    }
  };

  const handleNextPageClick = () => {
    const nextPage = currentPageValue + 1;
    setPage(nextPage);
    scrollToRendition(nextPage);
  };

  const handlePreviousPageClick = () => {
    const prevPage = currentPageValue - 1;
    scrollToRendition(prevPage);
    setPage(prevPage);
  };

  const handleListScroll = (e: React.UIEvent<HTMLUListElement>) => {
    if (!scrolling) {
      return;
    }
    let scrollIndex = 0;
    const element = e.currentTarget;
    const scrollTop = element.scrollTop;
    const scrollHeight = element.scrollHeight;
    const clientHeight = element.clientHeight;
    const scrolledToBottom = scrollTop + clientHeight >= scrollHeight;

    // filter out null values in renditionRefs array so as to provide for accurate calculations
    const filteredRenditionRefs = renditionRefs.current.filter(
      (ref) => ref.current != null
    );

    filteredRenditionRefs.forEach((ref, index, array) => {
      // setting to last page if scrolled to bottom (for full page view)
      if (scrolledToBottom) {
        return (scrollIndex = array.length - 1);
      }
      const currentRef = ref.current;
      if (currentRef && scrollTop >= currentRef.offsetTop) {
        return (scrollIndex = index);
      }
    });
    // only setting page if not not already set
    if (scrollIndex != sequence) {
      setPage(scrollIndex + 1);
    }
  };

  React.useEffect(() => {
    setCurrentPageValue(initialPageValue);
    setSequence(initialPageValue - 1);
    scrollToRendition(initialPageValue);
    setFullWidth();
  }, [asset]);

  React.useEffect(() => {
    scrollToThumbnail(sequence);
  }, [sequence]);

  React.useEffect(() => {
    /* This timeout delay fixes an issue with Safari and IE
     * where the page would scroll to the rendition
     * before the page layout had updated.
     */
    setTimeout(() => {
      scrollToRendition(currentPageValue);
    });
  }, [isFullWidth]);

  return (
    <div className="mv-asset-viewer mv-asset-viewer__container">
      <div className="mv-asset-viewer__rendition-viewer">
        <div className="mv-asset-viewer__header">
          <h2 className="mv-asset-viewer__header-title u-truncate-text">
            {asset.file.fileName}
          </h2>
          <div className="mv-asset-viewer__page-selector">
            <Button
              disabled={sequence === 0}
              onClick={handlePreviousPageClick}
              className="mv-asset-viewer__button"
            >
              <Icon
                className="mv-asset-viewer__header-icon-nav"
                glyph={IconGlyph.LEFT_OPEN}
              />
            </Button>
            <span>Page:</span>
            <span className="mv-asset-viewer__header-input-container">
              <input
                className="mv-asset-viewer__header-input"
                type="number"
                value={currentPageValue}
                onChange={handleInputChange}
                onBlur={handleInputBlur}
                onKeyDown={handleKeyDown}
                min={1}
                max={selectedRendition?.pages.count}
              />
            </span>
            <span>of {selectedRendition?.pages.count}</span>
            <Button
              disabled={sequence + 1 === selectedRendition?.pages.count}
              onClick={handleNextPageClick}
              className="mv-asset-viewer__button"
            >
              <Icon
                className="mv-asset-viewer__header-icon-nav"
                glyph={IconGlyph.RIGHT_OPEN}
              />
            </Button>
          </div>
          <Button
            title={isFullWidth ? "Whole Page" : "Page Width"}
            onClick={onToggleWidth}
            className="mv-asset-viewer__button"
          >
            <Icon
              className="mv-asset-viewer__header-icon-toggle"
              glyph={
                isFullWidth ? IconGlyph.DOCUMENT : IconGlyph.DOCUMENT_FULL_WIDTH
              }
            />
          </Button>
        </div>
        <div className="mv-asset-viewer__renditions-container">
          <div className={thumbnailStyles()}>
            <ul>
              {smallRendition?.pages.renditions.map((rendition, index) => (
                <ThumbnailImage
                  ref={thumbnailRefs.current[index]}
                  height={smallRendition.height}
                  src={rendition.url}
                  className={index === sequence ? "active" : ""}
                  onClick={() => handleThumbnailClick(index)}
                  label={index + 1}
                  key={rendition.url}
                />
              ))}
            </ul>
            <Button
              onClick={onToggleDrawer}
              className="mv-asset-viewer__button mv-asset-viewer__drawer"
            >
              <Icon
                className="mv-asset-viewer__drawer-icon"
                glyph={isDrawerOpen ? IconGlyph.LEFT_DIR : IconGlyph.RIGHT_DIR}
              />
            </Button>
          </div>
          <div className="mv-asset-viewer__renditions">
            <ul
              ref={renditionParentRef}
              onScroll={handleListScroll}
              onMouseEnter={() => setIsScrolling(true)}
              onMouseLeave={() => setIsScrolling(false)}
            >
              {selectedRendition?.pages.renditions.map((rendition, index) => {
                // setting client height. defaulting height to zero while the parent element does not exist.
                const clientHeight =
                  renditionParentRef.current?.clientHeight || 0;
                // setting toggled style depending on full width or full page
                const style = isFullWidth
                  ? { width: "100%", height: "auto" }
                  : {
                      height: clientHeight,
                      width: "auto",
                    };
                return (
                  <RenditionImage
                    ref={renditionRefs.current[index]}
                    key={rendition.url}
                    height={clientHeight}
                    style={style}
                    src={rendition.url}
                    label={index + 1}
                  />
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenditionViewer;
